import type {
  ResumeCertificationEntry,
  ResumeDraft,
  ResumeEducationEntry,
  ResumeExperienceEntry,
  ResumeProjectEntry,
  ResumeSkillEntry,
} from '../../lib/resume/types';
import type { FactState } from '../../lib/profile/types';
import { Button, FactBadge, Tooltip, controlClass } from '../ui';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '../ui/icons';

function RowActions({
  index,
  count,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Tooltip label="Move up">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Up"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          icon={<ArrowUpIcon />}
        />
      </Tooltip>
      <Tooltip label="Move down">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Down"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          icon={<ArrowDownIcon />}
        />
      </Tooltip>
      <Tooltip label="Remove from draft">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Remove from draft"
          onClick={onRemove}
          icon={<TrashIcon />}
        />
      </Tooltip>
    </div>
  );
}

const inputClass = controlClass;

function EntryShell({
  children,
  index,
  count,
  state,
  edited,
  onMove,
  onRemove,
}: {
  children: React.ReactNode;
  index: number;
  count: number;
  state: FactState;
  edited: boolean;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-md border border-line bg-surface-sunken p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FactBadge state={state} edited={edited} />
        <RowActions index={index} count={count} onMove={onMove} onRemove={onRemove} />
      </div>
      <div className="mt-3 grid gap-2">{children}</div>
    </div>
  );
}

export function SummaryEditor({
  draft,
  setSummary,
}: {
  draft: ResumeDraft;
  setSummary: (value: string) => void;
}) {
  return (
    <div>
      <FactBadge state={draft.summary.state} />
      <textarea
        aria-label="Resume summary"
        value={draft.summary.value}
        onChange={(e) => setSummary(e.target.value)}
        className={`${controlClass} mt-2 min-h-20 leading-relaxed`}
      />
    </div>
  );
}

export function ExperienceEditor({
  draft,
  update,
  move,
  remove,
}: {
  draft: ResumeDraft;
  update: (id: string, patch: Partial<Omit<ResumeExperienceEntry, 'id' | 'state' | 'evidence' | 'edited'>>) => void;
  move: (id: string, delta: -1 | 1) => void;
  remove: (id: string) => void;
}) {
  const entries = draft.experience;
  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <EntryShell
          key={entry.id}
          index={index}
          count={entries.length}
          state={entry.state}
          edited={entry.edited}
          onMove={(d) => move(entry.id, d)}
          onRemove={() => remove(entry.id)}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              aria-label="Role"
              placeholder="Role"
              value={entry.role}
              onChange={(e) => update(entry.id, { role: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Employer"
              placeholder="Employer"
              value={entry.organization}
              onChange={(e) => update(entry.id, { organization: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Location"
              placeholder="Location"
              value={entry.location}
              onChange={(e) => update(entry.id, { location: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Dates"
              placeholder="Dates as shown on the resume"
              value={entry.periodRaw}
              onChange={(e) => update(entry.id, { periodRaw: e.target.value })}
              className={inputClass}
            />
          </div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink-soft">Responsibilities (one per line)</span>
            <textarea
              aria-label="Responsibilities"
              value={entry.responsibilities.join('\n')}
              onChange={(e) =>
                update(entry.id, {
                  responsibilities: e.target.value.split('\n').filter((line) => line.trim().length > 0),
                })
              }
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink-soft">Achievements (one per line)</span>
            <textarea
              aria-label="Achievements"
              value={entry.achievements.join('\n')}
              onChange={(e) =>
                update(entry.id, {
                  achievements: e.target.value.split('\n').filter((line) => line.trim().length > 0),
                })
              }
              className={inputClass}
            />
          </label>
        </EntryShell>
      ))}
      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-4 text-center text-sm text-ink-faint">
          No experience copied from the profile.
        </p>
      ) : null}
    </div>
  );
}

export function EducationEditor({
  draft,
  update,
  move,
  remove,
}: {
  draft: ResumeDraft;
  update: (id: string, patch: Partial<Omit<ResumeEducationEntry, 'id' | 'state' | 'evidence' | 'edited'>>) => void;
  move: (id: string, delta: -1 | 1) => void;
  remove: (id: string) => void;
}) {
  const entries = draft.education;
  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <EntryShell
          key={entry.id}
          index={index}
          count={entries.length}
          state={entry.state}
          edited={entry.edited}
          onMove={(d) => move(entry.id, d)}
          onRemove={() => remove(entry.id)}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              aria-label="Institution"
              placeholder="Institution"
              value={entry.institution}
              onChange={(e) => update(entry.id, { institution: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Credential"
              placeholder="Credential"
              value={entry.credential}
              onChange={(e) => update(entry.id, { credential: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Field of study"
              placeholder="Field of study"
              value={entry.area}
              onChange={(e) => update(entry.id, { area: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Dates"
              placeholder="Dates as shown on the resume"
              value={entry.periodRaw}
              onChange={(e) => update(entry.id, { periodRaw: e.target.value })}
              className={inputClass}
            />
          </div>
        </EntryShell>
      ))}
      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-4 text-center text-sm text-ink-faint">
          No education copied from the profile.
        </p>
      ) : null}
    </div>
  );
}

export function SkillsEditor({
  draft,
  update,
  move,
  remove,
}: {
  draft: ResumeDraft;
  update: (id: string, patch: Partial<Omit<ResumeSkillEntry, 'id' | 'state' | 'evidence' | 'edited'>>) => void;
  move: (id: string, delta: -1 | 1) => void;
  remove: (id: string) => void;
}) {
  const entries = draft.skills;
  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <EntryShell
          key={entry.id}
          index={index}
          count={entries.length}
          state={entry.state}
          edited={entry.edited}
          onMove={(d) => move(entry.id, d)}
          onRemove={() => remove(entry.id)}
        >
          <input
            aria-label="Skill"
            placeholder="Skill"
            value={entry.name}
            onChange={(e) => update(entry.id, { name: e.target.value })}
            className={inputClass}
          />
        </EntryShell>
      ))}
      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-4 text-center text-sm text-ink-faint">
          No skills copied from the profile.
        </p>
      ) : null}
    </div>
  );
}

export function CertificationsEditor({
  draft,
  update,
  move,
  remove,
}: {
  draft: ResumeDraft;
  update: (id: string, patch: Partial<Omit<ResumeCertificationEntry, 'id' | 'state' | 'evidence' | 'edited'>>) => void;
  move: (id: string, delta: -1 | 1) => void;
  remove: (id: string) => void;
}) {
  const entries = draft.certifications;
  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <EntryShell
          key={entry.id}
          index={index}
          count={entries.length}
          state={entry.state}
          edited={entry.edited}
          onMove={(d) => move(entry.id, d)}
          onRemove={() => remove(entry.id)}
        >
          <div className="grid gap-2 sm:grid-cols-3">
            <input
              aria-label="Certification"
              placeholder="Certification"
              value={entry.name}
              onChange={(e) => update(entry.id, { name: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Issuer"
              placeholder="Issuer"
              value={entry.issuer}
              onChange={(e) => update(entry.id, { issuer: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Date"
              placeholder="Date as shown on the resume"
              value={entry.date}
              onChange={(e) => update(entry.id, { date: e.target.value })}
              className={inputClass}
            />
          </div>
        </EntryShell>
      ))}
      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-4 text-center text-sm text-ink-faint">
          No certifications copied from the profile.
        </p>
      ) : null}
    </div>
  );
}

export function ProjectsEditor({
  draft,
  update,
  move,
  remove,
}: {
  draft: ResumeDraft;
  update: (id: string, patch: Partial<Omit<ResumeProjectEntry, 'id' | 'state' | 'evidence' | 'edited'>>) => void;
  move: (id: string, delta: -1 | 1) => void;
  remove: (id: string) => void;
}) {
  const entries = draft.projects;
  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <EntryShell
          key={entry.id}
          index={index}
          count={entries.length}
          state={entry.state}
          edited={entry.edited}
          onMove={(d) => move(entry.id, d)}
          onRemove={() => remove(entry.id)}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              aria-label="Project name"
              placeholder="Project name"
              value={entry.name}
              onChange={(e) => update(entry.id, { name: e.target.value })}
              className={inputClass}
            />
            <input
              aria-label="Project URL"
              placeholder="URL"
              value={entry.url}
              onChange={(e) => update(entry.id, { url: e.target.value })}
              className={inputClass}
            />
          </div>
          <textarea
            aria-label="Project description"
            placeholder="Description"
            value={entry.description}
            onChange={(e) => update(entry.id, { description: e.target.value })}
            className={inputClass}
          />
          <input
            aria-label="Dates"
            placeholder="Dates as shown on the resume"
            value={entry.periodRaw}
            onChange={(e) => update(entry.id, { periodRaw: e.target.value })}
            className={inputClass}
          />
        </EntryShell>
      ))}
      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-4 text-center text-sm text-ink-faint">
          No projects copied from the profile.
        </p>
      ) : null}
    </div>
  );
}
